/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { usePlayer } from "@packages/media";
import type { MouseEventHandler, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "react-use";

import { colors, constantSemanticColors } from "../../../global/emotion";
import { useDynamicViewport } from "../../hooks/useDynamicViewport";
import { emotionDynamics } from "../../lib/emotionDynamics";
import { useMediaClass } from "../utils/media.emotion";

const defaultStyle: Interpolation = {
  backgroundColor: constantSemanticColors.fixedDarkenMedLow,
  inset: 0,
  opacity: 0,
  position: "fixed",
  transition: "opacity 150ms ease-in-out",
};
const hoveredStyle: Interpolation = {
  opacity: 1,
};
const childStyle: Interpolation = {
  background: colors.black,
  inset: 0,
  position: "absolute",
};

export const useFullscreenCatcher = (): [
  ReactNode,
  boolean,
  MouseEventHandler,
  () => void,
  () => void,
] => {
  const player = usePlayer();
  const [showControls, setShowControls] = useState<boolean>(true);
  const [updateControlsState, setUpdateControlsState] =
    useState<boolean>(showControls);
  const hideTimer = useRef<NodeJS.Timeout>(null);
  const isDesktop = useDynamicViewport({ minimumWidth: 1024 });
  const dontHide = useRef<boolean>(false);
  const mediaClass = useMediaClass();
  useDebounce(() => setShowControls(updateControlsState), 50, [
    updateControlsState,
  ]);

  const startHideCountdown = () => {
    if (dontHide.current) return;
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(hide, 3000);
  };

  const hide = () => setUpdateControlsState(false);

  useEffect(() => {
    if (
      mediaClass.includes("fullscreen") ||
      (mediaClass.includes("expanded") && mediaClass.includes("video"))
    ) {
      resumeHiding();
      startHideCountdown();
    } else {
      stopHiding();
      setUpdateControlsState(true);
    }
  }, [mediaClass]);

  const clickHandler: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      if (isDesktop) {
        if (player?.paused) player?.play();
        else player?.pause();
        setUpdateControlsState(true);
        startHideCountdown();
      } else if (showControls) {
        setUpdateControlsState(false);
        clearTimeout(hideTimer.current);
      } else {
        setUpdateControlsState(true);
        startHideCountdown();
      }
    },
    [showControls, player?.paused, isDesktop],
  );

  const hoverHandler = () => {
    setUpdateControlsState(true);
    startHideCountdown();
  };

  const stopHiding = () => {
    clearTimeout(hideTimer.current);
    dontHide.current = true;
  };

  const resumeHiding = () => {
    if (
      mediaClass.includes("fullscreen") ||
      (mediaClass.includes("expanded") && mediaClass.includes("video"))
    ) {
      dontHide.current = false;
      startHideCountdown();
    }
  };

  const catcher = (
    <div
      key={"fullscreenCatcher"}
      css={[defaultStyle, showControls ? hoveredStyle : null]}
      onClick={clickHandler}
      onMouseMove={hoverHandler}
      tabIndex={0}
      onFocus={hoverHandler}
    >
      <div
        key={"fullscreenCatcherChild"}
        css={[
          childStyle,
          emotionDynamics.invertMask({
            // nomenclature here is confusing -- the "to bottom" is effectively "from top," so it's the gradient at the top
            directions: ["to top", "to bottom"],
            spreads: [
              mediaClass.includes("desktop")
                ? 200
                : mediaClass.includes("landscape")
                  ? 0
                  : 325,
              mediaClass.includes("desktop") ? 150 : 90,
            ],
          }),
        ]}
      />
    </div>
  );

  return [catcher, showControls, hoverHandler, stopHiding, resumeHiding];
};
